import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../../App";
import { useCurrency } from "../../contexts/CurrencyContext";
import axios from "axios";
import Cookies from "js-cookie";
import { Tooltip as InfoTooltip } from "../ui/Tooltip";
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const VarianceAnalytics = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [breakdownType, setBreakdownType] = useState("volume");
  
  const [dashboardData, setDashboardData] = useState(null);
  const [breakdownData, setBreakdownData] = useState(null);
  const [rootCausesData, setRootCausesData] = useState(null);
  const [actionsData, setActionsData] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentPlan?.id) {
      fetchAllData();
    }
  }, [currentPlan]);

  useEffect(() => {
    if (currentPlan?.id && activeTab === "breakdown") {
      fetchBreakdownData(breakdownType);
    }
  }, [breakdownType, currentPlan, activeTab]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = Cookies.get("enakid_token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      };
      
      const [dashboardRes, rootCausesRes, actionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/actual-data/variance-dashboard/${currentPlan.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/variance-root-causes/${currentPlan.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/corrective-actions/${currentPlan.id}`, { headers })
      ]);
      
      setDashboardData(dashboardRes.data);
      setRootCausesData(rootCausesRes.data);
      setActionsData(actionsRes.data);
      
      // Fetch initial breakdown data
      await fetchBreakdownData("volume");
    } catch (err) {
      console.error("Error fetching variance analytics:", err);
      setError(err.response?.data?.detail || "Failed to load variance analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchBreakdownData = async (type) => {
    try {
      const token = Cookies.get("enakid_token");
      const response = await axios.get(
        `${BACKEND_URL}/api/actual-data/variance-breakdown/${currentPlan.id}?breakdown_type=${type}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      setBreakdownData(response.data);
    } catch (err) {
      console.error("Error fetching breakdown data:", err);
    }
  };

  const formatCurrency = (value) => {
    return formatCurrencyContext(value || 0, currentPlan?.currency);
  };

  const getVarianceColor = (status) => {
    return status === "favorable" ? "text-green-600" : "text-red-600";
  };

  const getVarianceIcon = (status) => {
    return status === "favorable" ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading Variance Analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <InfoTooltip content="Variance Analytics - Compare actual vs planned performance. Shows gaps in revenue, costs, and volume. Positive revenue variance = Exceeded target (good!). Negative cost variance = Under budget (saved money, good!). Use weekly for tracking, monthly for strategic reviews.">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">📊 Variance Analytics</h1>
          </InfoTooltip>
          <p className="text-slate-600">Analyze variances and identify corrective actions</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-xl">
            <InfoTooltip content="Variance Dashboard - Overview of total variance, revenue/cost/volume gaps, waterfall breakdown, and top 10 largest variances. Use for quick health check and executive reporting.">
              <TabsTrigger value="dashboard">Variance Dashboard</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="Variance Breakdown - Detailed drill-down by type: Volume (quantity), Price (pricing), Mix (product mix), Timing (when occurred), Unforeseen (unexpected). Shows contribution by item with root cause analysis.">
              <TabsTrigger value="breakdown">Variance Breakdown</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="Corrective Actions - Action plan register with owners, due dates, and priority matrix. Track accountability and progress. Every material variance (>$10K or >5%) should have defined action.">
              <TabsTrigger value="actions">Corrective Actions</TabsTrigger>
            </InfoTooltip>
          </TabsList>

          {/* Tab 1: Variance Dashboard */}
          <TabsContent value="dashboard" className="space-y-6">
            {dashboardData && (
              <>
                {/* Total Variance Card */}
                <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <InfoTooltip content="Total Variance - Net sum of all variances combined. Formula: (Actual Revenue - Planned Revenue) + (Planned Costs - Actual Costs). Positive (green) = Favorable (exceeded plan). Negative (red) = Unfavorable (missed plan). Shows overall business performance vs plan.">
                        <p className="text-sm text-gray-600 mb-2">🎯 Total Variance</p>
                      </InfoTooltip>
                      <div className="flex items-center justify-center gap-3">
                        <span className={`text-4xl font-bold ${getVarianceColor(dashboardData.total_variance.status)}`}>
                          {formatCurrency(dashboardData.total_variance.amount)}
                        </span>
                        <div className={getVarianceColor(dashboardData.total_variance.status)}>
                          {getVarianceIcon(dashboardData.total_variance.status)}
                        </div>
                      </div>
                      <p className={`text-lg mt-2 ${getVarianceColor(dashboardData.total_variance.status)}`}>
                        {dashboardData.total_variance.status === "favorable" ? "↑" : "↓"} {Math.abs(dashboardData.total_variance.percentage).toFixed(1)}%
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Variance Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader>
                      <InfoTooltip content="Revenue Variance - Actual vs planned revenue gap. Formula: Actual Revenue - Planned Revenue. Positive (green) = Exceeded target. Negative (red) = Missed target. Example: Planned $500K, Actual $550K = +$50K (+10%) beat plan. Caused by: Volume, Price, Mix, Timing, or Unforeseen factors.">
                        <CardTitle className="text-lg">Revenue Variance</CardTitle>
                      </InfoTooltip>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-2xl font-bold ${getVarianceColor(dashboardData.revenue_variance.status)}`}>
                            {formatCurrency(dashboardData.revenue_variance.amount)}
                          </p>
                          <p className={`text-sm ${getVarianceColor(dashboardData.revenue_variance.status)}`}>
                            {dashboardData.revenue_variance.percentage > 0 ? "+" : ""}{dashboardData.revenue_variance.percentage.toFixed(1)}%
                          </p>
                        </div>
                        <div className={getVarianceColor(dashboardData.revenue_variance.status)}>
                          {getVarianceIcon(dashboardData.revenue_variance.status)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <InfoTooltip content="Cost Variance - Actual vs planned cost gap. Formula: Actual Costs - Planned Costs. OPPOSITE OF REVENUE: Negative (green) = Favorable (under budget, saved money!). Positive (red) = Unfavorable (over budget, overspent). Example: Planned $300K, Actual $270K = -$30K (-10%) saved 10%.">
                        <CardTitle className="text-lg">Cost Variance</CardTitle>
                      </InfoTooltip>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-2xl font-bold ${getVarianceColor(dashboardData.cost_variance.status)}`}>
                            {formatCurrency(Math.abs(dashboardData.cost_variance.amount))}
                          </p>
                          <p className={`text-sm ${getVarianceColor(dashboardData.cost_variance.status)}`}>
                            {dashboardData.cost_variance.percentage > 0 ? "+" : ""}{dashboardData.cost_variance.percentage.toFixed(1)}%
                          </p>
                        </div>
                        <div className={getVarianceColor(dashboardData.cost_variance.status)}>
                          {getVarianceIcon(dashboardData.cost_variance.status)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <InfoTooltip content="Volume Variance - Actual vs planned quantity gap. Formula: Actual Units - Planned Units. Positive (green) = Sold more than planned. Negative (red) = Sold less than planned. Example: Planned 10K units, Actual 11.5K = +1.5K (+15%). Isolates quantity effect from price/mix.">
                        <CardTitle className="text-lg">Volume Variance</CardTitle>
                      </InfoTooltip>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-2xl font-bold ${getVarianceColor(dashboardData.volume_variance.status)}`}>
                            {dashboardData.volume_variance.units > 0 ? "+" : ""}{dashboardData.volume_variance.units.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-600">units</p>
                        </div>
                        <div className={getVarianceColor(dashboardData.volume_variance.status)}>
                          {getVarianceIcon(dashboardData.volume_variance.status)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Variance Waterfall Chart */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Variance Waterfall - Visual breakdown from Planned to Actual across 5 types: Volume (quantity impact), Price (pricing impact), Mix (product mix shift), Timing (when occurred), Unforeseen (unexpected). Green bars above = Favorable. Red bars below = Unfavorable. Shows HOW you got from plan to actual.">
                      <CardTitle>📊 Variance Waterfall Chart</CardTitle>
                    </InfoTooltip>
                    <CardDescription>Breakdown: Volume | Price | Mix | Timing | Unforeseen</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={dashboardData.waterfall_data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} />
                        <YAxis tickFormatter={(value) => formatCurrency(value)} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="value" fill="#3b82f6">
                          {dashboardData.waterfall_data.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.type === 'positive' ? '#10b981' : entry.type === 'negative' ? '#ef4444' : '#3b82f6'} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Top 10 Variances Table */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Top 10 Variances - Ranked list of 10 largest variances by dollar amount. Focus here first (Pareto: top 10 often = 80% of total variance). Shows: Item, Type (Revenue/Cost), Planned, Actual, Variance ($), % Variance, Status. >5% = material, >10% = critical, >25% = urgent action.">
                      <CardTitle>📋 Top 10 Variances</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-semibold">Item</th>
                            <th className="text-center p-3 font-semibold">Type</th>
                            <th className="text-right p-3 font-semibold">Planned</th>
                            <th className="text-right p-3 font-semibold">Actual</th>
                            <th className="text-right p-3 font-semibold">Variance</th>
                            <th className="text-right p-3 font-semibold">%</th>
                            <th className="text-center p-3 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.top_variances.map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3 font-medium">{item.item_name}</td>
                              <td className="p-3 text-center">
                                <Badge className={item.type === 'Revenue' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}>
                                  {item.type}
                                </Badge>
                              </td>
                              <td className="p-3 text-right">{formatCurrency(item.planned)}</td>
                              <td className="p-3 text-right font-medium">{formatCurrency(item.actual)}</td>
                              <td className={`p-3 text-right font-bold ${getVarianceColor(item.status)}`}>
                                {formatCurrency(item.variance_amount)}
                              </td>
                              <td className={`p-3 text-right ${getVarianceColor(item.status)}`}>
                                {item.variance_pct > 0 ? "+" : ""}{item.variance_pct.toFixed(1)}%
                              </td>
                              <td className="p-3 text-center">
                                <Badge className={item.status === 'favorable' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                  {item.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Tab 2: Variance Breakdown */}
          <TabsContent value="breakdown" className="space-y-6">
            {/* Type Selector */}
            <Card>
              <CardHeader>
                <InfoTooltip content="Select Breakdown Type - Choose variance category to analyze: Volume (quantity), Price (pricing), Mix (product/customer mix), Timing (period shifts), Unforeseen (unexpected). Start with largest variance from Waterfall chart.">
                  <CardTitle>Select Breakdown Type</CardTitle>
                </InfoTooltip>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {["volume", "price", "mix", "timing", "unforeseen"].map((type) => (
                    <button
                      key={type}
                      onClick={() => setBreakdownType(type)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        breakdownType === type
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)} Variance
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {breakdownData && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <Card>
                    <CardHeader>
                      <InfoTooltip content="Contribution by Item - Pie chart showing which items drive variance. Larger slices = Bigger contributors (focus here). For unfavorable variance, largest slice = biggest problem. For favorable, largest slice = biggest win. Use to prioritize analysis.">
                        <CardTitle>📊 Contribution by Item</CardTitle>
                      </InfoTooltip>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={breakdownData.pie_data}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={(entry) => `${entry.name}: ${entry.percentage.toFixed(1)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {breakdownData.pie_data.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Summary Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-600">Breakdown Type</p>
                          <p className="text-2xl font-bold capitalize">{breakdownData.breakdown_type}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Variance</p>
                          <p className="text-2xl font-bold">{formatCurrency(breakdownData.total_variance)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Number of Items</p>
                          <p className="text-2xl font-bold">{breakdownData.breakdown_data.length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed List */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Detailed Breakdown - Item-by-item analysis: Planned, Actual, Variance ($), %, and Explanation. Start with largest variances. Group by explanation to find systemic causes. Every material item (>$10K) needs root cause and action plan.">
                      <CardTitle>📋 Detailed Breakdown</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-semibold">Item</th>
                            <th className="text-right p-3 font-semibold">Planned</th>
                            <th className="text-right p-3 font-semibold">Actual</th>
                            <th className="text-right p-3 font-semibold">Variance</th>
                            <th className="text-right p-3 font-semibold">%</th>
                            <th className="text-left p-3 font-semibold">Explanation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {breakdownData.breakdown_data.map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3">{item.item}</td>
                              <td className="p-3 text-right">{item.planned.toLocaleString()}</td>
                              <td className="p-3 text-right font-medium">{item.actual.toLocaleString()}</td>
                              <td className={`p-3 text-right font-bold ${item.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {item.variance > 0 ? "+" : ""}{item.variance.toLocaleString()}
                              </td>
                              <td className={`p-3 text-right ${item.variance_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {item.variance_pct > 0 ? "+" : ""}{item.variance_pct.toFixed(1)}%
                              </td>
                              <td className="p-3 text-sm text-gray-600">{item.explanation}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Root Causes Analysis */}
                {rootCausesData && (
                  <>
                    <Card>
                      <CardHeader>
                        <InfoTooltip content="Root Cause Analysis - Bar chart showing frequency of root causes across variances. Tallest bars = Most frequent causes (systemic issues). Categories: Market Conditions, Execution, Planning, Resources, Timing, Strategy. Focus on high-frequency causes for maximum impact.">
                          <CardTitle>Root Cause Analysis</CardTitle>
                        </InfoTooltip>
                        <CardDescription>By Cause Type</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={rootCausesData.bar_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="cause" angle={-45} textAnchor="end" height={100} />
                            <YAxis label={{ value: 'Frequency', angle: -90, position: 'insideLeft' }} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="frequency" fill="#3b82f6" name="Frequency" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Root Causes Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {rootCausesData.root_causes.map((cause, idx) => (
                        <Card key={idx}>
                          <CardHeader>
                            <CardTitle className="text-lg">{cause.cause}</CardTitle>
                            <CardDescription>
                              Frequency: {cause.frequency} | Impact: {formatCurrency(cause.total_impact)}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {cause.items.map((item, itemIdx) => (
                                <div key={itemIdx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                  <span className="text-sm">{item.item}</span>
                                  <span className={`text-sm font-medium ${item.impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatCurrency(item.impact)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </TabsContent>

          {/* Tab 3: Corrective Actions */}
          <TabsContent value="actions" className="space-y-6">
            {actionsData && (
              <>
                {/* Actions Table */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Corrective Actions - Action register with Variance, Root Cause, Owner (DRI), Action (specific steps), Due Date, and Status. Every material variance (>$10K or >5%) needs action. Review weekly, track completion, validate impact after completion.">
                      <CardTitle>📋 Corrective Actions</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-semibold">Variance</th>
                            <th className="text-left p-3 font-semibold">Root Cause</th>
                            <th className="text-left p-3 font-semibold">Owner</th>
                            <th className="text-left p-3 font-semibold">Action</th>
                            <th className="text-center p-3 font-semibold">Due Date</th>
                            <th className="text-center p-3 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {actionsData.actions.map((action, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3">{action.variance}</td>
                              <td className="p-3">{action.root_cause}</td>
                              <td className="p-3">{action.owner}</td>
                              <td className="p-3">{action.action}</td>
                              <td className="p-3 text-center">{action.due_date}</td>
                              <td className="p-3 text-center">
                                <Badge className={
                                  action.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  action.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }>
                                  {action.status.replace('_', ' ')}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Priority Matrix */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Priority Matrix - Stacked bar chart: Urgency (orange) + Impact (blue). Taller bars = Higher priority (do first). Ideal = Both high urgency AND high impact. Use to sequence actions: Tallest first, shortest last (or skip low-priority).">
                      <CardTitle>🎯 Priority Matrix: Urgency vs Impact</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={actionsData.priority_matrix}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="action" angle={-45} textAnchor="end" height={120} />
                        <YAxis label={{ value: 'Priority Score', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="urgency" stackId="a" fill="#f59e0b" name="Urgency" />
                        <Bar dataKey="impact" stackId="a" fill="#3b82f6" name="Impact" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Gantt Timeline */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Action Timeline - Gantt view showing action duration (start to due date) and status. Bar colors: Green = Completed, Blue = In Progress, Gray = Not Started. Use for: Weekly reviews (on track?), Resource planning (capacity conflicts?), Forecasting (when will variance improve?).">
                      <CardTitle>📊 Action Timeline</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {actionsData.gantt_data.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="w-48 text-sm font-medium truncate">{item.action}</div>
                          <div className="flex-1 relative h-8 bg-gray-100 rounded">
                            <div 
                              className={`absolute h-full rounded ${
                                item.status === 'completed' ? 'bg-green-500' :
                                item.status === 'in_progress' ? 'bg-blue-500' : 'bg-gray-400'
                              }`}
                              style={{ left: '10%', width: '60%' }}
                            >
                              <span className="text-xs text-white px-2 leading-8">
                                {item.start_date} - {item.due_date}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default VarianceAnalytics;
